
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import BlogPostCard from './BlogPostCard';
import { Skeleton } from '@/components/ui/skeleton';
import { BookOpen, Sparkles } from 'lucide-react';

type BlogPost = Tables<'blog_posts'>;

interface RecentPostsProps {
  currentPostId: string;
}

const RecentPosts: React.FC<RecentPostsProps> = ({ currentPostId }) => {
  const { data: posts, isLoading } = useQuery<BlogPost[]>({
    queryKey: ['recent_blog_posts', currentPostId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .not('id', 'eq', currentPostId)
        .order('published_at', { ascending: false })
        .limit(3);

      if (error) {
        throw new Error(error.message);
      }
      return data || [];
    },
  });

  if (!isLoading && (!posts || posts.length === 0)) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500 p-8">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
            <BookOpen className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-2xl md:text-3xl font-bold text-white">More Articles</h2>
              <Sparkles className="w-5 h-5 text-white/80" />
            </div>
            <p className="text-emerald-100 text-sm md:text-base">Continue your reading journey with our latest insights</p>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-8">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-gray-50 rounded-2xl overflow-hidden">
                <Skeleton className="aspect-[16/10] w-full" />
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <Skeleton className="h-6 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3 mb-4" />
                  <div className="border-t border-gray-200 pt-4">
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts?.map((post) => (
              <BlogPostCard key={post.id} post={post} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentPosts;
